// json-editor-keymap.ts — PSTUDIO-1433
import type { KeyBinding } from '@pypestream/design-system';

/**
 * A CodeMirror command, narrowed to what this module needs: it is handed the
 * editor view and reports whether it handled the key. Declared structurally so
 * this helper needs no `@codemirror/*` import of its own — the design system
 * owns the single copy of those packages (two copies of `@codemirror/state`
 * break CodeMirror at runtime), and the real command is injected by the panel.
 */
export type EditorCommand = NonNullable<KeyBinding['run']>;

export interface JsonEditorKeyHandlers {
  /** Enter — commit the cell (and move the selection, decided by the caller). */
  onCommit: () => void;
  /** Shift+Enter — open/close the Expanded Cell View panel. */
  onTogglePanel: () => void;
  /** Cmd/Ctrl+Enter — the CodeMirror command that inserts a line break. */
  insertNewline: EditorCommand;
  /** Tab — the CodeMirror command that adds one indentation level. */
  indentMore: EditorCommand;
  /** Shift+Tab — the CodeMirror command that removes one indentation level. */
  indentLess: EditorCommand;
}

/**
 * PSTUDIO-1433: the JSON panel editor's key contract, as agreed with product
 * (Edan Soroker, Jira 25 Aug) after the manual test found the shipped editor
 * failing the ticket's "saves on cell commit only" requirement:
 *
 *   Enter            → commit
 *   Cmd/Ctrl+Enter   → new line within the cell
 *   Shift+Enter      → open/close the Expanded Cell View
 *   Tab              → inline editing indent (Shift+Tab outdents)
 *
 * Tab is bound here for EXPLICITNESS, not because the editor lacks it:
 * `@uiw/react-codemirror` enables `indentWithTab` by default (`useCodeMirror.js`
 * → `getDefaultExtensions.js`), so Tab already indented. What actually broke
 * contract point 4 was the GRID: its capture-phase Tab handler claimed the key
 * before CodeMirror ever saw it (measured live on 29 Aug — the document was
 * byte-identical across the press). The real fix is `shouldGridHandleTabCommit`;
 * this binding just states the contract at top precedence so a change to the
 * default setup cannot silently drop it.
 *
 * This inverts what the editor did before, where Enter inserted a newline and
 * the value only committed on blur. Each binding returns `true` so CodeMirror's
 * own binding for the same key never also runs; the extension is given top
 * precedence by `createKeymapExtension`.
 *
 * Kept a pure builder — no store access, no CodeMirror import — so the contract
 * is unit-testable without mounting an editor (pro-studio has no RTL).
 */
export function buildJsonEditorKeyBindings(
  handlers: JsonEditorKeyHandlers
): KeyBinding[] {
  return [
    {
      key: 'Enter',
      preventDefault: true,
      run: () => {
        handlers.onCommit();
        return true;
      },
    },
    {
      key: 'Shift-Enter',
      preventDefault: true,
      run: () => {
        handlers.onTogglePanel();
        return true;
      },
    },
    {
      // `Mod-` is Cmd on macOS and Ctrl elsewhere — exactly the platform split
      // the product decision spells out.
      key: 'Mod-Enter',
      preventDefault: true,
      run: (view) => handlers.insertNewline(view),
    },
    {
      // …and Ctrl+Enter ALSO inserts a line break on macOS, where `Mod-` covers
      // only Cmd. The product wording names Ctrl for Windows, but a Mac user on
      // an external PC keyboard (or coming from any other editor) reaches for
      // Ctrl+Enter too, and without this it fell through to CodeMirror's own
      // binding — which does something else entirely. Harmless elsewhere: on
      // Windows/Linux `Mod-Enter` IS Ctrl+Enter, and the first matching binding
      // wins, so this one simply never runs there.
      key: 'Ctrl-Enter',
      preventDefault: true,
      run: (view) => handlers.insertNewline(view),
    },
    {
      key: 'Tab',
      preventDefault: true,
      run: (view) => handlers.indentMore(view),
      shift: (view) => handlers.indentLess(view),
    },
    {
      // Tab indents (contract point 4), which means Tab can no longer move
      // focus OUT of the editor — a keyboard trap, and WCAG 2.1.2 requires an
      // escape discoverable by keyboard alone. CodeMirror's standard remedy on
      // its standard shortcut.
      // ONE binding for both platforms, deliberately — no `mac:` variant.
      // CodeMirror's own default keymap pairs `Ctrl-m` with `Shift-Alt-m` on
      // Mac, and that mac half DOES NOT WORK: `runHandlers` skips the keyCode
      // fallback whenever `browser.mac && event.altKey && !ctrlKey && !metaKey`
      // ("Alt-combinations on macOS tend to be typed characters",
      // @codemirror/view dist/index.js), and Shift+Option+M on a Mac layout
      // reports `event.key === "Â"` — so the candidate names are `Alt-Â` /
      // `Shift-Alt-Â` and nothing matches. On a Mac only `b.mac` is registered,
      // so declaring it would leave the Tab trap with NO escape at all, which
      // is the WCAG 2.1.2 failure this binding exists to prevent. Ctrl does not
      // rewrite `event.key` on macOS, so the plain `Ctrl-m` reaches the editor
      // on every platform. (An e2e cannot catch this: Playwright synthesises
      // `key: "m"` for Shift+Alt+M, so the broken binding tests green.)
      key: 'Ctrl-m',
      preventDefault: true,
      run: (view) => {
        // A TOGGLE (no argument), which is what CodeMirror binds to this same
        // shortcut. The millisecond form exists upstream too, but it is bound
        // to ESCAPE — "I am about to press Tab" — and Escape never reaches this
        // editor: the grid claims it in the capture phase to close the panel.
        // A 2s window measured live is too short for the case this serves:
        // someone navigating by keyboard presses the escape, then travels with
        // Tab, and the window expires mid-journey (measured 29 Aug — Tab
        // indented again a few seconds after the shortcut). The toggle is
        // reversible by the same keystroke, so it is not a trap: press it again
        // and Tab indents once more.
        view.setTabFocusMode();
        return true;
      },
    },
  ];
}
